// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useLayoutEffect, useRef, useState, type FocusEvent } from 'react';

import {
  clearStoreyFilter,
  pickStoreyFilter,
  showStoreyPicker,
  storeyPickerItems,
  toggleStoreyFilterMode,
  type StoreyLabelOptions,
} from '../../bygning/storeyPicker';
import type { Storey } from '../../bygning/storeys';
import type { StoreyFilterState } from '../../viewport/elementFilter';
import { XIcon } from '../IconButton';
import styles from './StoreyPicker.module.css';

export interface StoreyPickerProps {
  /** The building's storeys (`useStoreys` / `deriveStoreys`); fewer than two hides the picker. */
  storeys: readonly Storey[];
  /** The clipping box's storey filter — the same state Modelindstillinger › Geometri's Etage control used to own. */
  value: StoreyFilterState;
  onChange: (next: StoreyFilterState) => void;
  /** The floor-numbering convention (frontend-4o5, i18n); defaults to Danish/european ("S", 1, 2, …). */
  labelOptions?: StoreyLabelOptions;
}

/** "Kun denne etage": one filled bar, nothing above or below it kept. */
function OnlyFloorIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="3" y="6.3" width="10" height="3.4" rx="1" fill="currentColor" />
    </svg>
  );
}

/** "Denne etage og under": the bar, with a downward chevron for what it keeps beneath it. */
function AndBelowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <rect x="3" y="2.3" width="10" height="2.6" rx="1" fill="currentColor" />
      <path d="M4.3 8.6l3.7 3.4 3.7-3.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * The floor/storey picker over the 3D/plan viewer, bottom-right — Google
 * Maps' indoor level picker: collapsed to one small square button (the
 * clipped storey, or the ground storey with no clip set), its short
 * icon-like label ("S"/"G", "1", "2", "-1", …, `storeyShortLabel` — the full
 * name stays as the tooltip/aria-label). Hover, or click/focus for touch and
 * keyboard, unfolds it into the full vertical stack, highest storey on top,
 * the clipped one filled with the accent, followed by two more small
 * squares: the mode toggle ("Kun denne etage" ↔ "Denne etage og under",
 * shown only once a storey is picked) and "Alle etager" (clears the clip).
 * It folds back on mouse leave, blur, or a row/icon picked. Reports its own
 * height on `--viewport-corner-reserve` (`document.documentElement`, every
 * state) so `JobToaster`'s stack — the viewport's other bottom-right control
 * — shifts up rather than sitting under it; the property is a plain number
 * of pixels, 0 when the picker is not shown.
 */
export function StoreyPicker({ storeys, value, onChange, labelOptions }: StoreyPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const ignoreNextEnter = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const shown = showStoreyPicker(storeys);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!shown) {
      root.style.removeProperty('--viewport-corner-reserve');
      return;
    }
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const reserve = () => root.style.setProperty('--viewport-corner-reserve', `${el.offsetHeight}px`);
    reserve();
    const observer = new ResizeObserver(reserve);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--viewport-corner-reserve');
    };
    // `expanded` too: the reserve must grow/shrink with the fold and the mode-toggle row appearing.
  }, [shown, expanded]);

  if (!shown) return null;

  const items = storeyPickerItems(storeys, value.storeyId, labelOptions);
  const current = items.find((item) => item.selected) ?? items.find((item) => item.isGround) ?? items[0];
  const visible = expanded ? items : [current];

  // Focus moving to a row/icon inside the picker is not a blur; only focus
  // truly leaving it folds the stack (a click already folds it on pick).
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setExpanded(false);
  };

  const pick = (next: () => void) => {
    if (!expanded) {
      setExpanded(true);
      return;
    }
    next();
    // Folding removes rows below the one just clicked, so the box shrinks
    // from the bottom up — if a *lower* row ("Alle etager", the mode toggle)
    // was the one clicked, the shrunk box can end up back under a pointer
    // that never moved, and the browser re-fires mouseenter on it. Ignore
    // exactly that one synthetic re-entry; a later, real hover still opens it.
    ignoreNextEnter.current = true;
    setExpanded(false);
  };

  return (
    <div
      ref={ref}
      className={styles.picker}
      role="group"
      aria-label="Etage"
      aria-expanded={expanded}
      onMouseEnter={() => {
        if (ignoreNextEnter.current) {
          ignoreNextEnter.current = false;
          return;
        }
        setExpanded(true);
      }}
      onMouseLeave={() => {
        // A genuine leave means any later enter is a real, separate hover —
        // the one ghost re-entry this is guarding against never leaves first.
        ignoreNextEnter.current = false;
        setExpanded(false);
      }}
      onFocus={() => setExpanded(true)}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && expanded) {
          setExpanded(false);
          e.currentTarget.querySelector('button')?.focus();
        }
      }}
    >
      {visible.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`${styles.level}${item.selected ? ` ${styles.levelOn}` : ''}`}
          aria-pressed={item.selected}
          title={item.name}
          aria-label={item.name}
          onClick={() => pick(() => onChange(pickStoreyFilter(value, item.id)))}
        >
          {item.shortLabel}
        </button>
      ))}
      {expanded && value.storeyId !== null && (
        <button
          type="button"
          className={styles.level}
          aria-pressed={value.andBelow}
          title={value.andBelow ? 'Denne etage og under' : 'Kun denne etage'}
          aria-label={value.andBelow ? 'Denne etage og under' : 'Kun denne etage'}
          onClick={() => onChange(toggleStoreyFilterMode(value))}
        >
          {value.andBelow ? <AndBelowIcon /> : <OnlyFloorIcon />}
        </button>
      )}
      {expanded && (
        <button
          type="button"
          className={styles.level}
          disabled={value.storeyId === null}
          title="Alle etager"
          aria-label="Alle etager"
          onClick={() => pick(() => onChange(clearStoreyFilter()))}
        >
          <XIcon />
        </button>
      )}
    </div>
  );
}
